import type {
  AdminMatchDetail,
  AdminMatchListResponse,
  AdminMatchStatusFilter,
  GameMode,
} from '@shared/types';
import { api } from './api';

/**
 * Admin matches API client. Wraps the REST endpoints introduced in #286:
 *
 *   - GET /admin/matches?status=&page=&limit=   → paginated list, `status`
 *                                                ∈ {active, completed, forfeit}
 *   - GET /admin/matches/:id                    → detail + optional liveState
 *
 * Read-only. Admins do not terminate matches — players resolve their own
 * matches via the regular gameplay path.
 *
 * The service is thin on purpose — validation / normalisation lives in the
 * components. Keeping it dumb means the admin screens can mock via
 * `vi.mock('../../services/matches')` without wrestling with fetch internals.
 */

export interface ListMatchesParams {
  status?: AdminMatchStatusFilter;
  mode?: GameMode | '';
  /** Two-letter language code (en/tr). Empty = no filter. */
  language?: string;
  /** ObjectId; filters to matches where this id is player1 OR player2. */
  playerId?: string;
  /** Inclusive ISO lower bound on `startedAt`. */
  from?: string;
  /** Inclusive ISO upper bound on `startedAt`. */
  to?: string;
  page?: number;
  limit?: number;
}

/**
 * Build a query string, skipping empty / undefined values. Mirrors the helper
 * in `services/users.ts` — duplicated intentionally so each service stays
 * self-contained and can be unit-tested without a shared helper dependency.
 */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listMatches(
  params: ListMatchesParams = {},
): Promise<AdminMatchListResponse> {
  const qs = toQuery({
    status: params.status,
    mode: params.mode === '' ? undefined : params.mode,
    language: params.language,
    playerId: params.playerId,
    from: params.from,
    to: params.to,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
  return api<AdminMatchListResponse>(`/admin/matches${qs}`);
}

export async function getMatch(id: string): Promise<AdminMatchDetail> {
  return api<AdminMatchDetail>(`/admin/matches/${encodeURIComponent(id)}`);
}
